import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { MdWork, MdFolderShared, MdDescription, MdPeople, MdEvent, MdAdminPanelSettings, MdSettings } from 'react-icons/md';
import { MdOutlineWorkOutline, MdOutlineFolderShared, MdOutlineDescription, MdOutlinePeople, MdOutlineEvent, MdOutlineAdminPanelSettings, MdOutlineSettings } from "react-icons/md";
import { FiLogOut, FiList } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router';
import OrganisationSwitcher from '../organisation/OrganisationSwitcher';
import { can, homePathFor, isSuperAdminMode, PROFILE_UPDATED_EVENT, readSession, SUPER_ADMIN_CONSOLE_PATH } from '../../utils/organisationApi';

const drawerWidth = 80;

// permission: who sees the item once organisations are on (spec 3.2)
const allMenuItems = [
  { icon: <MdWork size={28} />, path: '/jobs', activeIcon: <MdOutlineWorkOutline size={28} />, ariaLabel: 'View jobs', label: 'Jobs', permission: 'hiring' },
  { icon: <MdFolderShared size={28} />, path: '/candidates', activeIcon: <MdOutlineFolderShared size={28} />, ariaLabel: 'View candidates', label: 'Candidates', permission: 'hiring' },
  { icon: <MdPeople size={28} />, path: '/interviewers', activeIcon: <MdOutlinePeople size={28} />, ariaLabel: 'View panelists', label: 'Panelists', permission: 'hiring' },
  { icon: <MdEvent size={28} />, path: '/interviews', activeIcon: <MdOutlineEvent size={28} />, ariaLabel: 'View interviews', label: 'Interviews' },
];

// In Super Admin mode there's no organisation, so the console is the only screen
const superAdminMenuItems = [
  { icon: <MdAdminPanelSettings size={28} />, path: SUPER_ADMIN_CONSOLE_PATH, activeIcon: <MdOutlineAdminPanelSettings size={28} />, ariaLabel: 'View all organisations', label: 'Organisations' },
];

export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [, setProfileVersion] = useState(0);
  const dropdownRef = useRef(null);
  const session = readSession();
  const homePath = homePathFor(session);
  const menuItems = isSuperAdminMode(session) ? superAdminMenuItems : allMenuItems.filter((item) => {
    if (item.orgsOnly && !session.orgsEnabled) return false;
    return !item.permission || can(session, item.permission);
  });

  const userDataStringify = localStorage.getItem("user_data");

  let user = {
    first_name: "User",
    last_name: "",
    email: "",
  };

  if (userDataStringify) {
    try {
      user = JSON.parse(userDataStringify) || user;
    } catch (error) {
      console.error("Error parsing user data:", error);
      localStorage.removeItem("user_data");
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [showDropdown]);

  // The name below is read from user_data, so show a saved profile change straight away
  useEffect(() => {
    const refresh = () => setProfileVersion((version) => version + 1);
    window.addEventListener(PROFILE_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, refresh);
  }, []);

  useEffect(() => {
    const focusDiv = document.getElementById('top-content');
    if (focusDiv) {
      focusDiv.focus();
    }
  }, [location.pathname]);

  // A sidebar item that already points into Settings (the Super Admin console) keeps the highlight
  const currentPath = `${location.pathname}${location.search}`;
  const settingsActive =
    location.pathname === '/settings' && !menuItems.some((item) => item.path.includes('?') && currentPath.startsWith(item.path));

  const goFromMenu = (path) => {
    setShowDropdown(false);
    navigate(path);
    if (isMobile) setOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const drawerContent = (
    <Box
      className="h-full flex flex-col justify-between items-center"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="w-full flex flex-col items-center">
        <Box
          className="flex items-center justify-center py-6 pb-10 cursor-pointer"
          onClick={() => {
            navigate(homePath);
            if (isMobile) setOpen(false);
          }}
        >
          <img src="/icons/logo-icon.png" alt="logo" width={30} height={25} />
        </Box>
        <List
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            px: 0,
          }}
        >
          {menuItems.map((item, index) => {
            const isActive =
              (item.path.includes("?") && `${location.pathname}${location.search}`.startsWith(item.path)) ||
              (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)) ||
              (item.path === "/jobs" && location.pathname === "/") ||
              (item.path === "/candidates" && location.pathname.startsWith("/candidate-list"));
            return (
              <ListItem key={index} disablePadding sx={{ width: 'auto' }}>
                <ListItemButton
                  aria-label={item.ariaLabel}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setOpen(false);
                  }}
                  className="flex flex-col items-center justify-center py-3 px-2"
                  sx={{
                    borderRadius: 2,
                    '&:hover': { backgroundColor: theme.palette.secondary.v2 },
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive
                        ? theme.palette.info.main
                        : theme.palette.secondary.main,
                      minWidth: 0,
                      margin: 0,
                    }}
                  >
                    {!isActive ? item.activeIcon : item.icon}
                  </ListItemIcon>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      color: isActive
                        ? theme.palette.info.main
                        : theme.palette.secondary.main,
                      textAlign: 'center',
                      lineHeight: 1.1,
                      whiteSpace: 'normal',
                      maxWidth: 72,
                    }}
                  >
                    {item.label}
                  </Typography>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </div>

      <Box className="flex flex-col items-center gap-2 w-full py-8">
        {can(session, 'hiring') && (
        <div className="w-full flex justify-center">
          <ListItem disablePadding sx={{ width: 'auto' }}>
            <ListItemButton
              aria-label="Template"
              onClick={() => {
                navigate('/standardized-template');
                if (isMobile) setOpen(false);
              }}
              className="flex flex-col items-center justify-center py-3 px-2"
              sx={{
                borderRadius: 2,
                '&:hover': { backgroundColor: theme.palette.secondary.v2 },
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === '/standardized-template'
                    ? theme.palette.info.main
                    : theme.palette.secondary.main,
                  minWidth: 0,
                  margin: 0,
                }}
              >
                {location.pathname === '/standardized-template' ? <MdDescription size={28} /> : <MdOutlineDescription size={28} />}
              </ListItemIcon>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: location.pathname === '/standardized-template'
                    ? theme.palette.info.main
                    : theme.palette.secondary.main,
                  textAlign: 'center',
                  lineHeight: 1,
                }}
              >
                Template
              </Typography>
            </ListItemButton>
          </ListItem>
        </div>
        )}

        <div className="w-full flex justify-center">
          <ListItem disablePadding sx={{ width: 'auto' }}>
            <ListItemButton
              aria-label="Settings"
              onClick={() => {
                navigate('/settings');
                if (isMobile) setOpen(false);
              }}
              className="flex flex-col items-center justify-center py-3 px-2"
              sx={{
                borderRadius: 2,
                '&:hover': { backgroundColor: theme.palette.secondary.v2 },
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <ListItemIcon
                sx={{
                  color: settingsActive ? theme.palette.info.main : theme.palette.secondary.main,
                  minWidth: 0,
                  margin: 0,
                }}
              >
                {settingsActive ? <MdSettings size={28} /> : <MdOutlineSettings size={28} />}
              </ListItemIcon>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: settingsActive ? theme.palette.info.main : theme.palette.secondary.main,
                  textAlign: 'center',
                  lineHeight: 1,
                }}
              >
                Settings
              </Typography>
            </ListItemButton>
          </ListItem>
        </div>

        <Box className="flex justify-center w-full">
        <div className="" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            aria-label='Profile'
            className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-lg hover:border-gray-400 transition"
            style={{ backgroundColor: theme.palette.secondary.v6 }}
          >
            {user?.first_name?.[0]?.toUpperCase() || "U"}
          </button>

          {showDropdown && (
            <div className="fixed left-2 bottom-20 mt-2 w-[18rem] max-h-[calc(100vh-6rem)] overflow-y-auto bg-white border border-gray-700 rounded-lg shadow-lg z-50">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700">
                <div className="w-12 h-12 flex items-center justify-center rounded-full text-white text-xl font-bold" style={{ backgroundColor: theme.palette.secondary.v6 }}>
                  {user?.first_name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="overflow-hidden">
                  <p className="font-semibold text-ellipsis whitespace-nowrap w-full">
                    {(user?.first_name || "") + " " + (user?.last_name || "")}
                  </p>
                  <p className="text-sm text-accent-gray text-ellipsis whitespace-nowrap w-full">
                    {user?.email || "No email"}
                  </p>
                </div>
              </div>

              {(session.isSuperAdmin || (session.orgsEnabled && session.role && !session.isSupportSession)) && (
                <OrganisationSwitcher session={session} />
              )}


              {/* {can(session, 'viewAuditLog') && (
                <button
                  onClick={() => goFromMenu('/organisation/activity')}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:!bg-accent-blue hover:text-white"
                >
                  <FiList className="text-lg" />
                  <span>Activity log</span>
                </button>
              )} */}

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-left hover:!bg-accent-blue hover:text-white"
              >
                <FiLogOut className="text-lg" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
        </Box>
      </Box>
    </Box>
  );

  // The support bar sits above the page for a Super Admin, except inside an organisation they belong to
  const supportBarShown = session.isSuperAdmin && (!session.organisationId || session.isSupportSession);

  const floatingMenu = isMobile && (
    <IconButton
      onClick={() => setOpen(true)}
      sx={{
        position: 'fixed',
        top: supportBarShown ? 62 : 24,
        left: 16,
        padding: 0,
        backgroundColor: 'transparent',
        color: 'black',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <MenuIcon />
    </IconButton>
  );

  return (
    <>
      {floatingMenu}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          zIndex: 40,
          '& .MuiDrawer-paper': {
            overflow: 'hidden',
            width: drawerWidth,
            backgroundColor: theme.palette.primary.main,
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${theme.palette.secondary.v4}`,
            borderWidth: '0 1px 0 0'
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}